import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BreadcrumbItem } from '../../components/breadcrumbs'
import { ItemDetails } from '../../models/item-details'
import { defaultItemsCount } from '../../settings/home-page-settings'

export type CheckoutResolvedBreadcrumbIds = {
  nonprofitCategoryId: number
  nonprofitId: number
} | null

type UseCheckoutBreadcrumbItemsParams = {
  itemDetails: ItemDetails | undefined
  resolvedNonprofitCategoryName: string | undefined
  resolvedBreadcrumbIds: CheckoutResolvedBreadcrumbIds
}

export const useCheckoutBreadcrumbItems = (
  params: UseCheckoutBreadcrumbItemsParams
): BreadcrumbItem[] => {
  const { itemDetails, resolvedNonprofitCategoryName, resolvedBreadcrumbIds } =
    params
  const { t } = useTranslation()

  return useMemo((): BreadcrumbItem[] => {
    const homeCrumb = { label: t('homePage.breadcrumbHome'), to: '/' as const }
    const searchCrumb = { label: t('header.menu.search'), to: '/search' as const }

    if (!itemDetails) {
      return [homeCrumb, searchCrumb, { label: t('checkout.pageTitle') }]
    }

    const nonprofit = itemDetails.nonprofit
    const categoryId =
      nonprofit?.nonprofitCategoryId ??
      resolvedBreadcrumbIds?.nonprofitCategoryId
    const nonprofitId =
      nonprofit?.nonprofitId ?? resolvedBreadcrumbIds?.nonprofitId
    const categoryName =
      nonprofit?.nonprofitCategoryName?.trim() || resolvedNonprofitCategoryName
    const categoryLabel = categoryName || '…'
    const nonprofitLabel = itemDetails.nonprofitName?.trim() || '—'
    const itemLabel = itemDetails.name?.trim() || '—'

    return [
      homeCrumb,
      searchCrumb,
      {
        label: categoryLabel,
        to:
          categoryId != null
            ? `/search?nonprofitCategoryId=${categoryId}`
            : undefined,
      },
      {
        label: nonprofitLabel,
        to:
          categoryId != null && nonprofitId != null
            ? `/search?pageIndex=1&pageSize=${defaultItemsCount}&nonprofitCategoryId=${categoryId}&nonprofitId=${nonprofitId}`
            : undefined,
      },
      {
        label: itemLabel,
        to: `/listed-items/${itemDetails.listedItemId}`,
      },
      { label: t('checkout.pageTitle') },
    ]
  }, [t, itemDetails, resolvedNonprofitCategoryName, resolvedBreadcrumbIds])
}
